import { useParams, useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/layout/PageHeader";
import { StatusPill } from "@/components/shared/StatusPill";
import { AICard } from "@/components/shared/AICard";
import { Loading } from "@/components/shared/Loading";
import { Button } from "@/components/ui/button";
import { useQuotation } from "@/api/hooks";
import { money } from "@/lib/format";
import { useApp } from "@/context/AppContext";
import { api } from "@/api/client";
import { toast } from "sonner";
import { ArrowLeft, MessageCircle, FileText, Check, History, ShieldCheck, Lock, ArrowUpRight } from "lucide-react";

// Phase 4.7 legacy-screen safety audit: this screen reads the pre-Phase-3
// `quotations` collection (built via /inquiries/{id}/build-quotation, a
// legacy path). It previously had a "Confirm & hand off" button that called
// POST /quotations/{id}/handoff — a route that (per docs/PHASE_3_...md Part
// 0) never actually created any Finance/Operations linkage, only an audit
// line, and was flagged "mock: true" server-side. Presenting that as a real
// verify-and-handoff action was misleading (Sales could believe an order had
// reached Finance when nothing happened) — a Class C finding. The button is
// removed; real acceptance verification only happens in the Phase 3/4.6
// commercial workspace (Workspace.jsx -> POST /commercial/acceptances/{id}/verify).
export default function QuotationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { openComposer } = useApp();
  const { data: q } = useQuotation(id);
  if (!q) return <Loading />;

  const parties = ["Customer · Primary contact"];
  const accepted = q.status === "Accepted";
  const openPdf = async () => {
    try {
      const response = await api.get(`/quotations/${q.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      window.open(url, "_blank", "noopener,noreferrer");
      window.setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Quotation PDF could not be generated"); }
  };

  return (
    <>
      <PageHeader
        title={<span className="flex items-center gap-2"><button onClick={() => navigate(-1)} className="text-slate-400 hover:text-slate-700" data-testid="qtn-back"><ArrowLeft className="h-4 w-4" /></button>{q.id} <span className="text-slate-300 font-normal">·</span> <span className="text-slate-500 font-normal text-base">{q.version}</span></span>}
        subtitle={`${q.customer} · ${q.lane}`}
        actions={<div className="flex items-center gap-1.5">
          <StatusPill status={q.status} className="mr-1" />
          <Button size="sm" variant="outline" className="h-8" onClick={() => openComposer({ channel: "whatsapp", parties, defaultParty: parties[0], customerId: q.customerId })} data-testid="qtn-whatsapp"><MessageCircle className="h-3.5 w-3.5 mr-1.5" />WhatsApp</Button>
        </div>} />

      <div className="flex-1 overflow-y-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 p-6">
          <div className="lg:col-span-2 space-y-4">
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3.5 flex items-center justify-between gap-3" data-testid="qtn-legacy-banner">
              <div className="text-[13px] text-amber-800">
                This is a legacy read-only quotation record. Sending, approval and acceptance verification for real
                commercial work now happen in the Sales workspace.
              </div>
              <Button size="sm" variant="outline" className="h-8 shrink-0 bg-white" onClick={() => navigate("/sales/pipeline")} data-testid="qtn-open-workspace">
                Open Sales workspace <ArrowUpRight className="h-3.5 w-3.5 ml-1.5" />
              </Button>
            </div>
            {q.aiNote && (
              <AICard title={accepted ? "Acceptance detected (historical)" : "Follow-up recommendation"} footer={
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => openComposer({ channel: "whatsapp", parties, defaultParty: parties[0], customerId: q.customerId })} data-testid="qtn-followup">Draft follow-up</Button>
              }>{q.aiNote}</AICard>
            )}

            <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50">
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-700"><FileText className="h-4 w-4 text-indigo-600" />Customer quotation</div>
                <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={openPdf} data-testid="qtn-pdf">Open PDF</Button>
              </div>
              <table className="w-full text-sm">
                <thead><tr className="text-[11px] uppercase tracking-wider text-slate-400 border-b border-slate-100"><th className="text-left font-semibold px-4 py-2">Charge</th><th className="text-right font-semibold px-4 py-2">Amount ({q.currency})</th></tr></thead>
                <tbody className="divide-y divide-slate-50">
                  {q.charges.map((ch) => (
                    <tr key={ch.name}>
                      <td className="px-4 py-2.5 text-slate-700">{ch.name}{ch.note && <span className="block text-[11px] text-amber-600">{ch.note}</span>}</td>
                      <td className="px-4 py-2.5 text-right font-mono-num text-slate-700">{ch.incl ? money(ch.sell, "") : <span className="text-slate-400">At actuals</span>}</td>
                    </tr>
                  ))}
                  <tr className="border-t-2 border-slate-200 bg-slate-50"><td className="px-4 py-2.5 font-semibold text-slate-800">Total selling price</td><td className="px-4 py-2.5 text-right font-mono-num font-bold text-slate-900">{money(q.sellTotal)}</td></tr>
                </tbody>
              </table>
              <div className="grid grid-cols-2 gap-4 p-4 border-t border-slate-100 text-sm">
                <div><div className="text-[11px] font-semibold uppercase tracking-wider text-emerald-600 mb-1">Inclusions</div><ul className="space-y-1 text-slate-600 text-[13px]">{q.inclusions.map((x) => <li key={x} className="flex gap-1.5"><Check className="h-3.5 w-3.5 text-emerald-500 mt-0.5 shrink-0" />{x}</li>)}</ul></div>
                <div><div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Exclusions</div><ul className="space-y-1 text-slate-500 text-[13px]">{q.exclusions.map((x) => <li key={x}>· {x}</li>)}</ul></div>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-3">Commercial</div>
              <div className="space-y-2.5 text-sm">
                <Row label="Validity" value={q.validity} /><Row label="Payment terms" value={q.paymentTerms} /><Row label="Currency" value={q.currency} />
                <Row label="Created" value={q.createdOn} /><Row label="Sent" value={`${q.sentOn} · ${q.channel}`} />
                {q.acceptedOn && <Row label="Accepted" value={q.acceptedOn} accent />}
              </div>
            </div>

            {q.costsRestricted ? (
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 flex items-center gap-2.5 text-sm text-slate-500"><Lock className="h-4 w-4 shrink-0" />Supplier cost & margin are restricted for your role.</div>
            ) : (
              <div className="rounded-xl border border-slate-200 bg-slate-900 text-slate-100 p-4">
                <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-3"><ShieldCheck className="h-3.5 w-3.5" />Internal · margin</div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <Metric label="Sell" value={money(q.sellTotal, "")} /><Metric label="Buy" value={money(q.buyTotal, "")} muted /><Metric label="GP" value={`${q.marginPct}%`} accent />
                </div>
              </div>
            )}

            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-3"><ShieldCheck className="h-3.5 w-3.5" />Approval checks</div>
              <div className="space-y-2">{q.approvals.map((a) => <div key={a.rule} className="flex items-start gap-2 text-[13px]"><Check className="h-3.5 w-3.5 text-emerald-500 mt-0.5 shrink-0" /><div><span className="text-slate-700">{a.rule}</span><span className="block text-[11px] text-slate-400">{a.result} · {a.by}</span></div></div>)}</div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-3"><History className="h-3.5 w-3.5" />Versions</div>
              <div className="space-y-2">{q.versions.map((v) => <div key={v.v} className="flex items-center justify-between text-[13px]"><div><span className="font-mono-num font-semibold text-slate-700">{v.v}</span><span className="text-slate-500 ml-2">{v.note}</span></div><span className="font-mono-num text-slate-400">{money(v.total, "")}</span></div>)}</div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

const Row = ({ label, value, accent }) => <div className="flex items-center justify-between gap-2"><span className="text-slate-400 text-[13px]">{label}</span><span className={`font-medium text-right ${accent ? "text-emerald-600" : "text-slate-700"}`}>{value}</span></div>;
const Metric = ({ label, value, accent, muted }) => <div><div className={`font-display font-bold text-lg font-mono-num ${accent ? "text-emerald-400" : muted ? "text-slate-400" : "text-white"}`}>{value}</div><div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div></div>;
